import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, ArrowRight, Globe, ClockClockwise } from "@phosphor-icons/react";
import { api, scoreColor } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";

const StatusBadge = ({ status, score }) => {
  if (status === "done") {
    const c = scoreColor(score);
    return (
      <span className="font-mono text-xs font-bold px-2.5 py-1 tabular-nums" style={{ color: c.hex, background: c.bghex }} data-testid="report-score-badge">
        {score} / 100
      </span>
    );
  }
  const map = {
    running: { t: "Çalışıyor", c: "#D97706", bg: "#FEF3C7" },
    pending: { t: "Bekliyor", c: "#52525B", bg: "#F4F4F5" },
    failed: { t: "Başarısız", c: "#E11D48", bg: "#FFE4E6" },
  };
  const m = map[status] || map.pending;
  return <span className="font-mono text-xs px-2.5 py-1" style={{ color: m.c, background: m.bg }}>{m.t}</span>;
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [reports, setReports] = useState(null);

  useEffect(() => {
    api.get("/reports").then((r) => setReports(r.data)).catch(() => setReports([]));
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <p className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2">Panel</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">Raporlarım</h1>
          </div>
          <button
            data-testid="new-report-btn"
            onClick={() => navigate("/yeni-rapor")}
            className="flex items-center gap-2 bg-brand text-white font-medium px-6 py-3 transition-transform hover:-translate-y-0.5"
          >
            <Plus size={20} weight="bold" /> Yeni Rapor Oluştur
          </button>
        </div>

        {reports === null ? (
          <div className="font-mono text-sm text-muted-foreground py-20 text-center animate-pulse">Yükleniyor…</div>
        ) : reports.length === 0 ? (
          <div className="border border-dashed border-border py-24 text-center" data-testid="empty-state">
            <Globe size={40} className="mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading text-xl font-semibold">Henüz raporun yok</h3>
            <p className="text-muted-foreground mt-2 max-w-md mx-auto">
              İlk raporun ücretsiz. Markanı gir, AI'ların seni önerip önermediğini öğren.
            </p>
            <button onClick={() => navigate("/yeni-rapor")}
              className="mt-6 inline-flex items-center gap-2 bg-foreground text-background px-5 py-2.5 font-medium">
              <Plus size={18} weight="bold" /> İlk raporunu oluştur
            </button>
          </div>
        ) : (
          <div className="border border-border divide-y divide-border" data-testid="reports-list">
            {reports.map((r) => (
              <button
                key={r.id}
                data-testid={`report-row-${r.id}`}
                onClick={() => navigate(`/rapor/${r.id}`)}
                className="w-full flex items-center gap-4 p-5 text-left hover:bg-muted/50 transition-colors group"
              >
                <div className="flex-1 min-w-0">
                  <h3 className="font-heading font-semibold text-lg truncate">{r.brand_name}</h3>
                  <p className="text-sm text-muted-foreground truncate">
                    {[r.sector, r.category].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <span className="font-mono text-xs text-muted-foreground hidden md:flex items-center gap-1.5">
                  <ClockClockwise size={14} />
                  {new Date(r.created_at).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" })}
                </span>
                <StatusBadge status={r.status} score={r.overall_score} />
                <ArrowRight size={18} className="text-muted-foreground group-hover:translate-x-1 transition-transform" />
              </button>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
